import React, { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, PerspectiveCamera as DreiPerspectiveCamera } from '@react-three/drei';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import styles from './MainPage.module.css';
import * as THREE from 'three';
import { motion } from 'framer-motion';

import { YepModel } from '../components/YepModel';
import { Stand } from '../components/Stand';

const heroTextVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7, delay: 0.6, ease: "easeOut" } },
};

const canvasAreaVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1.0, delay: 0.3, ease: "easeOut" } },
};

const gridContainerVariants = {
    hidden: { opacity: 1 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
        },
    },
};

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const MainPage = () => {
    const modelXOffsetInItsCanvas = 0;
    const standTopSurfaceY = 3;
    const manualModelOriginOffsetY =2.4;
    const standGeoProps = { geoHeight: 0.3, geoRadius: 2.5 };
    const standYPosition = standTopSurfaceY - (standGeoProps.geoHeight / 2);
    const standPosition = [modelXOffsetInItsCanvas, standYPosition, 0];
    const modelOriginY = standTopSurfaceY + manualModelOriginOffsetY;
    const modelProps = {
        modelEffectiveHeight: 2.0,
        isRotating: true,
        color: "#1A202C",
        metalness: 0.9,
        roughness: 0.4,
        scale: 1.1,
        position: [modelXOffsetInItsCanvas, modelOriginY, 0],
        rotation: [0,0,0]
    };

    const cameraTargetY = modelOriginY;
    const cameraTargetX = modelXOffsetInItsCanvas;
    const cameraFov = 60;
    const cameraInitialZ = 10;
    const cameraInitialPosition = [cameraTargetX, cameraTargetY + 0.5, cameraInitialZ];

    const uplightColor = "#8899BB";
    const lightYOnStand = standTopSurfaceY + 0.05;
    const ambientLightIntensity = 0.4;
    const directionalLightIntensity = 0.4;
    const directionalLightPosition = [cameraTargetX + 2, cameraTargetY + 8, 7];

    return (
        <div className={styles.pageWrapper}>
            <main className={styles.mainContentArea}>
                <section className={styles.heroSection} id="hero-section">
                    <motion.div
                        className={styles.canvasAreaOnLeft}
                        variants={canvasAreaVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <Canvas
                            shadows
                            gl={{
                                antialias: true,
                                powerPreference: "high-performance",
                            }}
                        >
                            <color attach="background" args={['#000000']} />
                            <DreiPerspectiveCamera makeDefault position={cameraInitialPosition} fov={cameraFov} near={0.1} far={100}/>
                            <ambientLight intensity={ambientLightIntensity} />
                            <directionalLight position={directionalLightPosition} intensity={directionalLightIntensity} castShadow shadow-mapSize-width={1024} shadow-mapSize-height={1024} shadow-bias={-0.0005}/>
                            {Array.from({ length: 8 }).map((_, i) => {
                                const angle = (i / 8) * Math.PI * 2;
                                const lightRingRadius = standGeoProps.geoRadius * 0.30;
                                const x = Math.cos(angle) * lightRingRadius;
                                const z = Math.sin(angle) * lightRingRadius;
                                return (
                                    <pointLight key={`uplight-${i}`} position={[x + standPosition[0], lightYOnStand, z + standPosition[2]]} color={uplightColor} intensity={2.0} distance={modelProps.modelEffectiveHeight * modelProps.scale + standGeoProps.geoHeight + 1.5} decay={2.0} />
                                );
                            })}
                            <Environment preset="studio" background={false} intensity={0.05} />
                            <Suspense fallback={null}>
                                <YepModel {...modelProps} />
                                <Stand position={standPosition} geoHeight={standGeoProps.geoHeight} geoRadius={standGeoProps.geoRadius} baseColor="#080808" emissiveDotColor="#666666" innerRingColor="#555555" />
                            </Suspense>
                            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[modelXOffsetInItsCanvas, standYPosition - standGeoProps.geoHeight / 2 - 0.01, 0]} receiveShadow>
                                <planeGeometry args={[standGeoProps.geoRadius * 2.5, standGeoProps.geoRadius * 2.5]} />
                                <shadowMaterial opacity={0.1} />
                            </mesh>
                            <OrbitControls enablePan={false} enableZoom={false} minPolarAngle={Math.PI / 1.9} maxPolarAngle={Math.PI / 1.9} target={[cameraTargetX, cameraTargetY, 0]} />
                            <EffectComposer>
                                <Bloom luminanceThreshold={1.4} luminanceSmoothing={0.7} intensity={0.5} mipmapBlur={true} />
                            </EffectComposer>
                        </Canvas>
                    </motion.div>
                    <motion.div
                        className={styles.heroTextContainer}
                        variants={heroTextVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <h2>Yep <span className={styles.dashSeparator}>—</span> <span className={styles.heroSubtitle}>сообщество возможностей</span></h2>
                        <p>
                            Название проекта говорит само за себя: Yep означает движение вперед, открытие новых горизонтов и создание пространства для роста. Мы объединяем людей, идеи и проекты, предлагая удобные и надежные сервисы для самых разных задач.
                        </p>
                        <p>
                            Наш принцип — развитие через сотрудничество. Мы поддерживаем инициативу, внедряем новые решения и стремимся к тому, чтобы каждый участник Yep находил свою нишу для самореализации и успеха.
                        </p>
                        <button className={styles.heroButton}>Узнать больше о Yep</button>
                    </motion.div>
                </section>

                <motion.section
                    className={styles.projectsSection}
                    id="projects"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={gridContainerVariants}
                >
                    <h3>Направления YEP</h3>
                    <div className={styles.projectsGrid}>
                        <motion.div className={styles.projectCard} variants={cardVariants}>
                            <h4>YEP<span className={styles.ampersand}>&</span>Link</h4>
                            <p>Yep&Link — это удобный инструмент для создания и управления проектами, чатами и группами. С нашей платформой вы можете легко организовать командную работу, обмениваться идеями, а также запускать группы по интересам или для решения специфических задач.</p>
                            <button className={styles.projectCardButton} onClick={() => window.open('https://lab5-efba9.firebaseapp.com/', '_blank')}>Перейти на сайт</button>
                        </motion.div>
                        <motion.div className={styles.projectCard} variants={cardVariants}>
                            <h4>YEP<span className={styles.ampersand}>&</span>Sell</h4>
                            <p>В Yep&Sell мы предлагаем не только широкий выбор игровых аккаунтов, но и платформу для безопасных сделок. Мы ориентируемся на удобство пользователей, предлагая простоту в поиске и покупке, а также обеспечиваем защиту и поддержку на каждом этапе. Это лишь начало, и мы будем продолжать расширять возможности для всех геймеров.</p>
                            <button className={styles.projectCardButton} onClick={() => window.open('https://yepandsell-55d88.web.app/', '_blank')}>Перейти на сайт</button>
                        </motion.div>
                        <motion.div className={styles.projectCard} variants={cardVariants}>
                            <h4>YEP<span className={styles.ampersand}>&</span>Boost</h4>
                            <p>Yep&Boost помогает игрокам прокачивать свои аккаунты, достигать новых уровней и улучшать характеристики. Мы гарантируем качественные и быстрые результаты, уделяя особое внимание безопасности и удовлетворению наших клиентов.</p>
                        </motion.div>
                        <motion.div className={styles.projectCard} variants={cardVariants}>
                            <h4>YEP<span className={styles.ampersand}>&</span>Set</h4>
                            <p>В Yep&Set мы разрабатываем стильные и эффективные дизайны, создавая визуальное решение для веб-сайтов, брендов и онлайн-пространства. Наш фокус — это простота, функциональность и адаптивность, что делает каждый проект не только красивым, но и удобным для пользователя.</p>
                        </motion.div>
                        <motion.div className={styles.projectCard} variants={cardVariants}>
                            <h4>YEP<span className={styles.ampersand}>&</span>Coding</h4>
                            <p>Пространство для разработчиков. API, SDK и песочница для создания собственных интеграций и расширения платформы. (Текст будет обновлен)</p>
                        </motion.div>
                        <motion.div className={styles.projectCard} variants={cardVariants}>
                            <h4>YEP<span className={styles.ampersand}>&</span>Future</h4>
                            <p>Исследуем новые горизонты. Инкубатор для самых смелых идей, использующих передовые технологии и ИИ. (Текст будет обновлен)</p>
                        </motion.div>
                    </div>
                </motion.section>
            </main>
        </div>
    );
};

export default MainPage;